import { useState, useRef, useContext, useCallback, useEffect } from "react";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import { Tooltip } from "@fluentui/react-components";
import { useFluentToast } from "../../../../../components/FluentUI/Fluent-Toast";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { LoginContext } from "../../../../../services/api/authentication-api";
import { useHistory } from "react-router-dom";
import { FluentInput } from "../../../../../components/FluentUI/Fluent-Input";
import PropTypes from "prop-types";
import {
  getLastUpdateTooltip,
  submitButton,
} from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import {
  getEditedRows,
  buildPowerPayload,
  getSummaryColumns,
  getDetailColumns,
  handleExport,
  getInitialDetailState,
  saveDetailStateToStorage,
  clearDetailStateFromStorage,
} from "./power-banker-helper";
import PowerBankerDetailGrid from "./power-banker-detail-grid";
import PowerBankerSummaryGrid from "./power-banker-summary-grid";
import { SavePowerBankerHours } from "../../../../../services/api/dashboard-api";
import "./power-banker-time-log.scss";

const PowerBankerTimeLog = ({ items }) => {
  const user = useContext(LoginContext);
  const [showDetails, setShowDetails] = useState(true);
  const [detailState, setDetailState] = useState(() => getInitialDetailState(user?.userID));
  const [isSaving, setIsSaving] = useState(false);
  const [data, setData] = useState(items.Projects || []);
  const [originalData, setOriginalData] = useState(data);
  const [searchValue, setSearchValue] = useState("");
  const [summaryItem, setSummaryItem] = useState(items);
  const [isVisibleDataEmpty, setIsVisibleDataEmpty] = useState(false);
  const [overallCapacityDraft, setOverallCapacityDraft] = useState( items?.OverallCapacity ?? null);
  const { showToast, FluentToast } = useFluentToast();
  const _detailExport = useRef(null);
  const _summaryExport = useRef(null);
  const searchValueRef = useRef(searchValue);
  const history = useHistory();
  const canCreate = user?.currentUser?.privileges?.["Projects"]?.Add ?? false;
  const canEdit = user?.currentUser?.privileges?.["Projects"]?.Edit ?? false;
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value.trimStart());
  };

  useEffect(() => {
    searchValueRef.current = searchValue;
    if (searchValue?.trim()) {
      clearDetailStateFromStorage(user?.userID);
    }
  }, [searchValue]);

  
  const handleModifySearchFocus = () => {
    const input = document.getElementById("power-search-input");
    if (input) {
      input.focus();
    }
  };

  const handleDetailStateChange = (newState) => {
    const userId = user?.userID;
    setDetailState(newState);
    saveDetailStateToStorage(userId, newState, searchValueRef.current);
  };

  const handleItemChange = (e) => {
    const { field, value, dataItem } = e;
    const restrictedFields = ["LastWeekHours", "WeekendHours", "NextWeekHours"];
    if (restrictedFields.includes(field) && value < 0) return;

    setData((prev) =>
      prev.map((row) =>
        row.ProjectId === dataItem.ProjectId ? { ...row, [field]: value } : row
      )
    );
  };

  const handleSubmit = async () => {
    const editedRows = getEditedRows(data, originalData);
    const hasDealEdits = editedRows.length > 0;
    const hasCapacityEdit = overallCapacityDraft !== summaryItem?.OverallCapacity;

    if (!hasDealEdits && !hasCapacityEdit) return;

    const payload = buildPowerPayload(editedRows);

    if (hasCapacityEdit) {
      if (!summaryItem?.OverallCapacityTaskId) {
        showToast("Save Failed", "Overall Capacity task not found.", "error");
        return;
      }
      payload.OverallCapacity = overallCapacityDraft ?? null;
      payload.OverallCapacityTaskId = summaryItem.OverallCapacityTaskId;
    }

    setIsSaving(true);
    try {
      const response = await SavePowerBankerHours(payload);
      const updated = response?.data;

      if (!updated) {
        showToast("Save Failed", "Could not save your changes.", "error");
        return;
      }

      if (Array.isArray(updated.Projects)) {
        const merged = updated.Projects;
        setData(merged);
        setOriginalData(merged);
      }

      setSummaryItem(updated);
      setOverallCapacityDraft(updated?.OverallCapacity ?? null);

      const now = new Date();
      showToast(
        "Edits Saved Successfully",
        `Changes saved on ${now.toLocaleDateString()} at ${now.toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        })}`,
        "success"
      );
    } catch (err) {
      console.error("Failed to submit:", err);
      showToast("Save Failed", "An unexpected error occurred. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleVisibleDataChange = useCallback(({ isEmpty, searchValue }) => {
    setIsVisibleDataEmpty(isEmpty && !!searchValue);
  }, []);

  const editedRows = getEditedRows(data, originalData);
  const hasCapacityEdit = overallCapacityDraft !== summaryItem?.OverallCapacity;
  const canSubmitCapacity = hasCapacityEdit && Boolean(summaryItem?.OverallCapacityTaskId);
  const isSubmitDisabled = isSaving || (editedRows.length === 0 && !canSubmitCapacity);
  const tooltipText = getLastUpdateTooltip(summaryItem?.LastUpdated);
  const submitBtn = submitButton(isSubmitDisabled, handleSubmit);

  const hasAnyFilter =
    detailState.filter &&
    Array.isArray(detailState.filter.filters) &&
    detailState.filter.filters.length > 0;

  const canReset = detailState.sort.length > 0 || hasAnyFilter;
  const resetDataState = () => {
    setSearchValue("");
    const resetState = { sort: [], filter: null };
    setDetailState(resetState);
    clearDetailStateFromStorage(user?.userID);
  };

  return (
    <>
      <div className="power-banker-time-log">
        <div className="widget-header">
          <div className="header-left">
            <div className="main-title-badge-row">
              <div className="main-title">Banker Time Log</div>
                <FluentInput
                  id="power-search-input"
                  placeholder="Search"
                  type="text"
                  value={searchValue}
                  onChange={handleSearchChange}
                  componentStyle="search-input"
                  contentBefore={<FluentUiIcon name="SearchRegular" size={14} />}
                  noKey
                />
            </div>
            <div className="sub-title">GIB/PU&I</div>
          </div>
          <div className="header-right">
            <Tooltip content="Reset Filters and Sorting">
              <FluentButton
                appearance="secondaryWithOutline"
                icon={
                  <FluentUiIcon
                    name="ArrowResetRegular"
                    color={canReset ? "#0F6CBD" : "#A0A0A0"}
                    size={20}
                    disabled={!canReset}
                  />
                }
                disabled={!canReset}
                onClick={resetDataState}
              />
            </Tooltip>

            <Tooltip content="Export Data">
              <FluentButton
                appearance="secondaryWithOutline"
                icon={
                  <FluentUiIcon
                    name="ArrowDownloadRegular"
                    color="#0F6CBD"
                    size={20}
                  />
                }
                onClick={() =>
                  handleExport({ summaryRef: _summaryExport, detailRef: _detailExport })
                }
              />
            </Tooltip>

            <FluentButton
              text="Create Project"
              appearance="secondaryWithOutline"
              disabled={!canCreate}
              icon={
                <FluentUiIcon
                  name="FolderAddRegular"
                  color={canCreate ? "#0F6CBD" : "#A0A0A0"}
                  size={20}
                />
              }
              onClick={() => {
                if (canCreate) {
                  history.push({
                    pathname: "/projects/create",
                    state: { fromDashboard: true },
                  });
                }
              }}
            />

            {tooltipText ? (
              <Tooltip content={tooltipText} relationship="label" positioning="below">
                <div>{submitBtn}</div>
              </Tooltip>
            ) : (
              submitBtn
            )}
          </div>
        </div>

        <ExcelExport data={[summaryItem]} ref={_summaryExport}>
          {getSummaryColumns()}
        </ExcelExport>

        {!searchValue && (
          <PowerBankerSummaryGrid
            summaryItem={{...summaryItem, OverallCapacity: overallCapacityDraft}}
            showDetails={showDetails}
            onToggle={() => setShowDetails(!showDetails)}
            onOverallCapacityChange={(newVal) => { setOverallCapacityDraft(newVal) }}
          />
        )}

         {showDetails && (
          <>
            <ExcelExport data={data} ref={_detailExport}>
              {getDetailColumns()}
            </ExcelExport>

            <PowerBankerDetailGrid
              data={data}
              onItemChange={handleItemChange}
              canEdit={canEdit}
              canCreate={canCreate}
              detailState={detailState}
              onDetailStateChange={handleDetailStateChange}
              searchValue={searchValue}
              onVisibleDataChange={handleVisibleDataChange}
              resetDataState={resetDataState}
              refineSearch={handleModifySearchFocus}
            />
            {/* Following div is added to bring focus to submit button if the user is tabbing through the widget */}
            <div
              tabIndex={0}
              style={{ width: 0, height: 0, overflow: "hidden" }}
              onFocus={() => {
                const btn = document.getElementById("tb-submit-btn");
                if (btn) btn.focus();
              }}
            />
          </>
        )}

      </div>
      {FluentToast}
    </>
  );
};

export default PowerBankerTimeLog;

PowerBankerTimeLog.propTypes = {
  items: PropTypes.shape({
      User: PropTypes.shape({
        Id: PropTypes.number.isRequired,
        Name: PropTypes.string.isRequired,
      }).isRequired,
      LastUpdated: PropTypes.string,
      OverallCapacity: PropTypes.string,
      OverallCapacityTaskId: PropTypes.number,
      TotalHoursEstimated: PropTypes.number,
      TotalHoursSpent: PropTypes.number,
      Projects: PropTypes.arrayOf(
        PropTypes.shape({
          GroupName: PropTypes.string.isRequired,
          ProjectId: PropTypes.number.isRequired,
          ProjectIdString: PropTypes.string.isRequired,
          TaskId: PropTypes.number.isRequired,
          ProjectName: PropTypes.string.isRequired,
          ClientName: PropTypes.string,
          IsNew: PropTypes.bool,
          IsOverdue: PropTypes.bool,
          LastUpdated: PropTypes.string,
          LastUpdatedString: PropTypes.string,
          LastWeekHours: PropTypes.number,
          WeekendHours: PropTypes.number,
          NextWeekHours: PropTypes.number,
        })
      ).isRequired,
    }),
  };